import { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Download, Link2, Printer, Share2 } from "lucide-react";
import { toast } from "sonner";
import { DocumentPreview } from "@/components/DocumentPreview";

export function useDocumentExport(doc) {
  const printRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const filename = `${doc?.number || "document"}.pdf`;

  const print = useReactToPrint({ contentRef: printRef, documentTitle: doc?.number || "Document" });

  const makePdf = async () => {
    const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const w = 210;
    const h = (canvas.height * w) / canvas.width;
    pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, w, Math.min(h, 297));
    return pdf;
  };

  const download = async () => {
    setBusy(true);
    try {
      (await makePdf()).save(filename);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setBusy(false);
    }
  };

  const share = async (shareUrl) => {
    setBusy(true);
    try {
      if (navigator.share) {
        const blob = (await makePdf()).output("blob");
        const file = new File([blob], filename, { type: "application/pdf" });
        const payload = navigator.canShare?.({ files: [file] }) ? { files: [file], title: doc.number } : { title: doc.number, url: shareUrl };
        await navigator.share(payload);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Share link copied to clipboard");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Sharing failed");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (shareUrl) => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Public link copied");
  };

  return { printRef, busy, print, download, share, copyLink, makePdf };
}

export function ExportButtons({ exp, shareUrl, compact }) {
  const cls = `btn-press h-10 px-4 text-sm font-semibold border border-border hover:border-foreground flex items-center gap-2 disabled:opacity-50`;
  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={exp.download} disabled={exp.busy} data-testid="download-pdf-button" className={`${cls} bg-primary text-primary-foreground border-primary hover:bg-[#C04E2D]`}>
        <Download className="w-4 h-4" strokeWidth={1.5} /> {compact ? "" : "Download PDF"}
      </button>
      <button onClick={exp.print} data-testid="print-button" className={cls}>
        <Printer className="w-4 h-4" strokeWidth={1.5} /> {compact ? "" : "Print"}
      </button>
      {shareUrl && (
        <>
          <button onClick={() => exp.share(shareUrl)} disabled={exp.busy} data-testid="share-button" className={cls}>
            <Share2 className="w-4 h-4" strokeWidth={1.5} /> {compact ? "" : "Share"}
          </button>
          <button onClick={() => exp.copyLink(shareUrl)} data-testid="copy-link-button" className={cls}>
            <Link2 className="w-4 h-4" strokeWidth={1.5} /> {compact ? "" : "Copy link"}
          </button>
        </>
      )}
    </div>
  );
}

export function HiddenPrintable({ exp, doc, settings, shareToken }) {
  return (
    <div className="fixed -left-[2000px] top-0" aria-hidden="true">
      <DocumentPreview ref={exp.printRef} doc={doc} settings={settings} shareToken={shareToken} />
    </div>
  );
}
